import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createColumnHelper, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { debounce } from "lodash-es";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router";

import { useApi } from "@/api/hooks/useApi";
import type { TicketCategoryDto } from "@/api/types";
import { Anchor } from "@/components/Anchor/Anchor";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { ContextMenu } from "@/components/ContextMenu/ContextMenu";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { CalendarDateIcon } from "@/components/Icons/CalendarDateIcon";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { GitBranch01Icon } from "@/components/Icons/GitBranch01Icon";
import { InfinityIcon } from "@/components/Icons/InfinityIcon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { XIcon } from "@/components/Icons/XIcon";
import { Table } from "@/components/Table/Table";
import { TagList, transformConstraintToTag } from "@/components/Tag/TagList";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useBool } from "@/hooks/useBool";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { useTicketCategoryQueries } from "@/queries/ticket-categories/queryOptions";
import { useTicketQueries } from "@/queries/tickets";
import { QUERY_KEYS } from "@/query-keys";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { canManageCategories } from "../permissions";

export function TicketCategoryList(): React.ReactNode {
  const projectId = useProjectId();
  const slug = useSlug();
  const { t } = useTranslation();
  const { componentProps, openDeleteModal } = useDeleteModal<string>("rr-delete-modal-modal");
  const hasPermission = usePermission();
  const showFlashToast = useFlashToast();
  const navigate = useNavigate();
  const api = useApi();
  const queryClient = useQueryClient();
  const [audienceHidden, audienceHiddenHandlers] = useBool();
  const ticketQueries = useTicketQueries();
  const { data: ticketCategories } = useSuspenseQuery(ticketQueries.categoryList());
  const ticketCategoryQueries = useTicketCategoryQueries();
  const { data: treeSettings } = useSuspenseQuery(ticketCategoryQueries.ticketCategoryTreeSettings());
  const deleteTicketCategoryMutationResult = useMutation({
    mutationFn: api.deleteTicketCategoriesByIdV1,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.TICKET_CATEGORIES(projectId),
      });
      showFlashToast({
        type: "success",
        title: t("model.repair-category.action.delete.notification.success"),
      });
    },
    onError: () => {
      showFlashToast({
        type: "error",
        title: t("model.repair-category.action.delete.notification.error"),
      });
    },
  });
  const hasTicketCategoryTree = ticketCategories.items.some((x) => x.type === "tree");
  const columns = useMemo(() => {
    const helper = createColumnHelper<TicketCategoryDto>();

    return [
      ...(hasTicketCategoryTree
        ? [
            helper.accessor("type", {
              header: "",
              cell: (cell) => cell.getValue() === "tree" && <GitBranch01Icon className="text-grey-700" />,
              maxSize: 40,
            }),
          ]
        : []),
      helper.accessor("name", {
        header: t("model.repair-category.name"),
        cell: (cell) =>
          hasPermission(canManageCategories) ? (
            <Anchor
              to={routes.ticketCategories.edit({
                slug,
                id: cell.row.original.id,
              })}
            >
              {cell.getValue()}
            </Anchor>
          ) : (
            cell.getValue()
          ),
      }),
      helper.accessor("defaultAssignee", {
        header: t("model.repair-category.assigned"),
        cell: (cell) => <span className="text-grey-700">{cell.getValue()?.fullName}</span>,
      }),
      helper.accessor("audience", {
        header: t("model.repair-category.audience"),
        cell: (cell) => {
          const audience = cell.getValue();
          if (audience.length === 0) {
            return <i className="text-grey-700">{t("model.constraint.placeholder")}</i>;
          }

          return <TagList tags={cell.getValue().map(transformConstraintToTag)} limit={3} />;
        },
      }),
      helper.accessor("notificationType", {
        header: t("model.repair-category.notification-type"),
        cell: (cell) =>
          cell.row.original.type === "single" && (
            <span className="text-grey-700">
              {cell.getValue() === "canBeCollective"
                ? t("model.repair-category.notification-type.collective")
                : t("model.repair-category.notification-type.private")}
            </span>
          ),
      }),
      helper.accessor("reopenPolicy", {
        header: t("model.repair-category.reopen-policy"),
        cell: (cell) => (
          <div className="flex justify-center">
            {cell.row.original.reopenPolicy === "oneWithinFourteenDays" ? <CalendarDateIcon /> : <InfinityIcon />}
          </div>
        ),
      }),
      helper.accessor("disabled", {
        header: t("model.repair-category.status"),
        cell: (cell) => (
          <div className="flex justify-center">
            {cell.getValue() ? <XIcon className="text-red-600" /> : <CheckIcon className="text-green-800" />}
          </div>
        ),
      }),
      helper.accessor("id", {
        header: "",
        cell: (cell) => {
          const menuActions: ContextMenuItem[] = [
            {
              labels: {
                default: t("common.action.edit"),
              },
              value: "edit",
              icon: <Edit05Icon />,
              callbackFn: () => {
                void navigate(
                  routes.ticketCategories.edit({
                    slug,
                    id: cell.getValue(),
                  }),
                );
              },
              isHidden: !hasPermission(canManageCategories),
            },
            {
              labels: {
                default: t("common.action.delete"),
              },
              value: "delete",
              icon: <Trash02Icon />,
              callbackFn: () => openDeleteModal(cell.getValue()),
              isHidden: !cell.row.original.canDelete || !hasPermission(canManageCategories),
            },
          ];

          return (
            <div className="flex justify-end">
              <ContextMenu items={menuActions} />
            </div>
          );
        },
      }),
    ];
  }, [hasTicketCategoryTree, t, hasPermission, slug, navigate, openDeleteModal]);
  const tableInstance = useReactTable<TicketCategoryDto>({
    columns,
    data: ticketCategories?.items ?? [],
    state: {
      columnVisibility: {
        audience: !audienceHidden,
      },
    },
    getCoreRowModel: getCoreRowModel(),
  });
  const hideColumnsOnMobile = useMemo(
    () =>
      debounce(() => {
        const windowWidth = getWindowSize().width;
        if (windowWidth <= SM_SCREEN_SIZE && !audienceHidden) {
          audienceHiddenHandlers.setTrue();
        }
        if (windowWidth > SM_SCREEN_SIZE && audienceHidden) {
          audienceHiddenHandlers.setFalse();
        }
      }, 200),
    [audienceHidden, audienceHiddenHandlers],
  );
  useEffect(() => {
    hideColumnsOnMobile();
    window.addEventListener("resize", hideColumnsOnMobile);

    return () => {
      window.removeEventListener("resize", hideColumnsOnMobile);
    };
  }, [hideColumnsOnMobile]);

  return (
    <>
      <DeleteModal
        title={t("model.repair-category.action.delete.confirmation")}
        description={t("model.repair-category.action.delete.description")}
        onDelete={deleteTicketCategoryMutationResult.mutateAsync}
        deleteBtnProps={{
          "data-testid": "repair-categories-action-delete",
        }}
        {...componentProps}
      />
      <div className="overflow-auto">
        {treeSettings.enabled && (
          <h3 className="mb-3 text-headline3">{t("page.ticket-categories.subtitle-with-tree")}</h3>
        )}
        <Table<TicketCategoryDto>
          table={tableInstance}
          data-testid="repair-categories-list"
          getRowProps={() => ({
            "data-testid": "repair-categories-item",
          })}
        />
      </div>
    </>
  );
}
const SM_SCREEN_SIZE = 767;
function getWindowSize() {
  return {
    width: window.innerWidth,
  };
}
